import { Controller } from "@hotwired/stimulus"
import { Html5Qrcode } from "html5-qrcode"

// Scanner controller — wraps html5-qrcode and POSTs decoded tokens to the
// existing /staff/redeem endpoint. Falls back gracefully (text input remains)
// if the camera can't be opened.
//
// Dev-only mock: when Rails.env === 'development' and the URL has
// ?mock=ok|err|dup, getUserMedia + BarcodeDetector are skipped; a synthetic
// result is dispatched after a short delay so the result UX can be validated
// in headless Chrome. Production code path is untouched.
export default class extends Controller {
  static targets = ["viewport", "result", "resultMessage", "resultBadge", "resultBadgeLabel", "tokenInput", "fallback", "startBtn", "feedbackToggle"]
  static values = {
    redeemUrl: String,
    mode: String,
    workshopId: String,
    timeslotId: String
  }

  connect() {
    this.scanner = null
    this.busy = false
    this.cooldownUntil = 0
    this.lastToken = null
    this.lastTokenAt = 0

    if (this.shouldRunMock()) {
      this.runMock()
    }
  }

  disconnect() {
    this.stop()
  }

  // -- Dev-only mock -------------------------------------------------------
  shouldRunMock() {
    if (document.body?.dataset?.railsEnv !== "development") return false
    const params = new URLSearchParams(window.location.search)
    return params.has("mock")
  }

  runMock() {
    const params = new URLSearchParams(window.location.search)
    const kind = params.get("mock")
    if (this.hasStartBtnTarget) this.startBtnTarget.hidden = true
    if (this.hasFallbackTarget) this.fallbackTarget.hidden = true

    setTimeout(() => {
      const body = this.mockBody(kind)
      this.showResult(body, this.stateForBody(body))
    }, 600)
  }

  mockBody(kind) {
    switch (kind) {
      case "ok":
        return { ok: true, message: "Bun venit, Maria!", order_item_id: 42, pass_type: "day_3", state: "ok" }
      case "dup":
        return { ok: false, message: "Acces deja folosit", order_item_id: 42, pass_type: "day_3", state: "dup" }
      case "err":
      default:
        return { ok: false, message: "Bilet necunoscut", order_item_id: null, state: "err" }
    }
  }

  // -----------------------------------------------------------------------

  async start() {
    if (this.scanner) return
    if (this.shouldRunMock()) return

    if (!this.hasViewportTarget) return

    if (!navigator.mediaDevices?.getUserMedia) {
      this.showResult({ message: "Camera nu este suportată pe acest dispozitiv." }, "err")
      return
    }

    try {
      this.scanner = new Html5Qrcode(this.viewportTarget.id, { verbose: false })
      const qrbox = (w, h) => {
        const s = Math.floor(Math.min(w, h) * 0.7)
        return { width: s, height: s }
      }
      await this.scanner.start(
        { facingMode: "environment" },
        { fps: 10, aspectRatio: 1.0, qrbox },
        (decodedText) => this.onScan(decodedText),
        () => {} // ignore decode failures (every frame without a code triggers this)
      )
      if (this.hasStartBtnTarget) this.startBtnTarget.hidden = true
      if (this.hasFallbackTarget) this.fallbackTarget.hidden = true
    } catch (err) {
      this.scanner = null
      console.warn("QR scanner failed to start:", err)
      this.showResult({ message: "Camera nu a putut fi pornită. Folosește câmpul de text de mai jos." }, "err")
      if (this.hasFallbackTarget) this.fallbackTarget.hidden = false
    }
  }

  async stop() {
    if (!this.scanner) return
    try {
      await this.scanner.stop()
      await this.scanner.clear()
    } catch (_) { /* noop */ }
    this.scanner = null
  }

  async onScan(token) {
    const now = Date.now()
    if (this.busy) return
    if (this.flashShowing()) return
    if (now < this.cooldownUntil) return
    if (token === this.lastToken && now - this.lastTokenAt < 3000) return

    this.busy = true
    this.lastToken = token
    this.lastTokenAt = now

    try {
      const body = await this.postToken(token)
      this.showResult(body, this.stateForBody(body))
      if (this.feedbackEnabled()) this.feedback(body.ok)
    } catch (err) {
      this.showResult({ message: "Eroare de rețea. Încearcă din nou." }, "err")
    }
    // busy stays true until the operator dismisses the flash; see dismiss().
  }

  // Form-fallback submit: same flow, just driven by a text input.
  async submit(event) {
    event.preventDefault()
    if (this.flashShowing()) return
    const token = this.tokenInputTarget.value.trim()
    if (!token) return

    this.busy = true

    try {
      const body = await this.postToken(token)
      this.showResult(body, this.stateForBody(body))
      if (this.feedbackEnabled()) this.feedback(body.ok)
    } catch (err) {
      this.showResult({ message: "Eroare de rețea. Încearcă din nou." }, "err")
    } finally {
      this.tokenInputTarget.value = ""
    }
  }

  flashShowing() {
    return this.hasResultTarget && this.resultTarget.hidden === false
  }

  // Operator confirms they've read the flash. Re-arm the scanner.
  dismiss() {
    this.clearResult()
    this.busy = false
    this.cooldownUntil = Date.now() + 500 // brief debounce so the same code doesn't immediately re-fire
    if (this.hasTokenInputTarget) this.tokenInputTarget.focus()
  }

  async postToken(token) {
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    const res = await fetch(this.redeemUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrf || "",
        "Accept": "application/json"
      },
      body: JSON.stringify({
        token,
        mode: this.modeValue,
        workshop_id: this.workshopIdValue || null,
        timeslot_id: this.timeslotIdValue || null
      })
    })
    return res.json()
  }

  // Map server payload → flash state. Server only carries ok/!ok; we infer
  // "dup" from the message when the failure was a re-scan. Mock branch sets
  // body.state explicitly so the UX can be exercised without a backend.
  stateForBody(body) {
    if (body?.state === "ok" || body?.state === "err" || body?.state === "dup") return body.state
    if (body?.ok) return "ok"
    if (typeof body?.message === "string" && /deja/i.test(body.message)) return "dup"
    return "err"
  }

  showResult(body, state) {
    if (!this.hasResultTarget) return
    const message = typeof body === "string" ? body : (body?.message ?? "")
    if (this.hasResultMessageTarget) {
      this.resultMessageTarget.textContent = message
    } else {
      this.resultTarget.textContent = message
    }
    this.resultTarget.dataset.state = state
    this.resultTarget.hidden = false

    if (this.hasResultBadgeTarget) {
      const passType = typeof body === "object" ? body?.pass_type : null
      const label = this.modeValue === "entrance" ? this.passLabel(passType) : null
      if (label) {
        this.resultBadgeTarget.dataset.passType = passType
        if (this.hasResultBadgeLabelTarget) this.resultBadgeLabelTarget.textContent = label
        this.resultBadgeTarget.hidden = false
      } else {
        delete this.resultBadgeTarget.dataset.passType
        if (this.hasResultBadgeLabelTarget) this.resultBadgeLabelTarget.textContent = ""
        this.resultBadgeTarget.hidden = true
      }
    }
  }

  clearResult() {
    if (!this.hasResultTarget) return
    if (this.hasResultMessageTarget) {
      this.resultMessageTarget.textContent = ""
    } else {
      this.resultTarget.textContent = ""
    }
    if (this.hasResultBadgeTarget) {
      delete this.resultBadgeTarget.dataset.passType
      if (this.hasResultBadgeLabelTarget) this.resultBadgeLabelTarget.textContent = ""
      this.resultBadgeTarget.hidden = true
    }
    delete this.resultTarget.dataset.state
    this.resultTarget.hidden = true
  }

  passLabel(passType) {
    if (passType === "day_1") return "Acces eveniment · 1 zi"
    if (passType === "day_3") return "Acces eveniment · 3 zile"
    return null
  }

  feedbackEnabled() {
    return this.hasFeedbackToggleTarget && this.feedbackToggleTarget.checked
  }

  feedback(ok) {
    if (navigator.vibrate) navigator.vibrate(ok ? 60 : [80, 60, 80])
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)()
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.frequency.value = ok ? 880 : 220
      gain.gain.value = 0.05
      osc.connect(gain).connect(ctx.destination)
      osc.start()
      osc.stop(ctx.currentTime + 0.12)
    } catch (_) { /* ignore */ }
  }
}
